import {
  AlertTriangle,
  CheckCircle,
  Circle,
  Loader2,
  XCircle,
} from 'lucide-react';
import { ReactNode } from 'react';

import { AddonHealthStatus, AddonLifecycleStatus } from '@api/types.gen';

import { Badge, BadgeType } from '@@/Badge';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

const TIME_MESSAGE = 'Usually takes 1-3 minutes.';

interface Props {
  lifecycleStatus?: AddonLifecycleStatus;
  lifecycleMessage?: string;
  healthStatus?: AddonHealthStatus;
  healthMessage?: string;
}

/**
 * AddonStatusBadge renders a single effective status for an addon, derived from
 * both its install/upgrade lifecycle and (when reported) its runtime health.
 *
 * The two axes are collapsed into one badge on purpose: a clean install shows
 * one green "Installed"/"Running" badge rather than a green "Installed" next to a
 * redundant green "Healthy", and an installed-but-broken addon shows a single
 * red "Unhealthy" instead of a confusing green + red pair. When something needs
 * explaining (a failed install, a rolled-back upgrade, a failing health check),
 * the tooltip carries a plain-language summary with the raw detail below it.
 */
export function AddonStatusBadge({
  lifecycleStatus,
  lifecycleMessage,
  healthStatus,
  healthMessage,
}: Props) {
  const { type, label, icon, summary, detail } = getStatusDescriptor(
    lifecycleStatus,
    lifecycleMessage,
    healthStatus,
    healthMessage
  );

  const badge = (
    <Badge
      type={type}
      data-cy="addon-status-badge"
      className="whitespace-nowrap"
    >
      {icon}
      {label}
    </Badge>
  );

  // Wrap in a tooltip only when there is something to explain. When a raw detail
  // exists the human summary becomes the tooltip heading and the detail sits
  // below it; otherwise the summary is the whole message.
  if (summary || detail) {
    return (
      <TooltipWithChildren
        heading={detail ? summary : ''}
        message={detail || summary || ''}
      >
        {badge}
      </TooltipWithChildren>
    );
  }

  return badge;
}

export type Descriptor = {
  type: BadgeType;
  label: string;
  icon: ReactNode;
  /** Plain-language explanation shown to the user. */
  summary?: string;
  /** Raw underlying detail (Helm error, probe failure) shown beneath the summary. */
  detail?: string;
};

const dot = <Circle className="h-2 w-2 shrink-0 fill-current" />;
const spinner = <Loader2 className="h-3 w-3 shrink-0 animate-spin" />;

/**
 * getStatusDescriptor collapses an addon's install/upgrade lifecycle and its
 * runtime health into one effective status. Shared by AddonStatusBadge and the
 * detail view's status stat block so the two never disagree.
 */
export function getStatusDescriptor(
  lifecycleStatus: AddonLifecycleStatus | undefined,
  lifecycleMessage: string | undefined,
  healthStatus: AddonHealthStatus | undefined,
  healthMessage: string | undefined
): Descriptor {
  // No lifecycle status means no Helm release: never installed.
  if (!lifecycleStatus) {
    return { type: 'muted', label: 'Not installed', icon: dot };
  }

  switch (lifecycleStatus) {
    // In-progress states can carry a progress detail in the message; surface it
    // in the tooltip when present.
    case AddonLifecycleStatus.ADDON_STATUS_INSTALLING:
      return {
        type: 'info',
        label: 'Installing',
        icon: spinner,
        detail: lifecycleMessage
          ? `${TIME_MESSAGE} ${lifecycleMessage}`
          : TIME_MESSAGE,
      };
    case AddonLifecycleStatus.ADDON_STATUS_UPGRADING:
      return {
        type: 'info',
        label: 'Upgrading',
        icon: spinner,
        detail: lifecycleMessage
          ? `${TIME_MESSAGE} ${lifecycleMessage}`
          : TIME_MESSAGE,
      };
    case AddonLifecycleStatus.ADDON_STATUS_UNINSTALLING:
      return {
        type: 'info',
        label: 'Uninstalling',
        icon: spinner,
        detail: lifecycleMessage,
      };
    case AddonLifecycleStatus.ADDON_STATUS_UNKNOWN:
      return {
        type: 'muted',
        label: 'Unknown',
        icon: dot,
        summary: 'Unable to get status.',
      };
    case AddonLifecycleStatus.ADDON_STATUS_FAILED:
      return {
        type: 'danger',
        label: 'Failed',
        icon: <XCircle className="h-3 w-3 shrink-0" />,
        summary: 'Installation failed.',
        detail: lifecycleMessage,
      };
    case AddonLifecycleStatus.ADDON_STATUS_INSTALLED:
      return installedDescriptor(lifecycleMessage, healthStatus, healthMessage);
    default:
      return { type: 'muted', label: 'Not installed', icon: dot };
  }
}

// installedDescriptor is where lifecycle and health combine into one status.
// Health and a rolled-back upgrade are independent, so the four combinations of
// (upgrade failed?) x (health) must each read differently: an addon that rolled
// back to a healthy previous version is a lesser problem than one that rolled
// back to a version that is itself not responding.
function installedDescriptor(
  lifecycleMessage: string | undefined,
  healthStatus: AddonHealthStatus | undefined,
  healthMessage: string | undefined
): Descriptor {
  // A message on an otherwise-installed addon means the last upgrade failed and
  // Helm rolled back to the previous version.
  const upgradeFailed = !!lifecycleMessage;

  // Unhealthy dominates: the addon is not working right now, which matters more
  // than a rolled-back upgrade. Surface it even when an upgrade also failed (the
  // version it rolled back to is itself unhealthy) so the two do not look alike.
  if (healthStatus === AddonHealthStatus.ADDON_HEALTH_UNHEALTHY) {
    return {
      type: 'danger',
      label: 'Unhealthy',
      icon: <XCircle className="h-3 w-3 shrink-0" />,
      summary: upgradeFailed
        ? 'The last upgrade failed and the addon is not responding to health checks.'
        : 'Installed but not responding to health checks.',
      detail: healthMessage || lifecycleMessage,
    };
  }

  // Upgrade failed but the addon is not reporting unhealthy: it rolled back and
  // is still serving the previous version, so warn rather than error, and state
  // the health we do know.
  if (upgradeFailed) {
    return {
      type: 'warn',
      label: 'Upgrade failed',
      icon: <AlertTriangle className="h-3 w-3 shrink-0" />,
      summary:
        healthStatus === AddonHealthStatus.ADDON_HEALTH_HEALTHY
          ? 'The last upgrade failed and was rolled back. The addon is healthy on the previous version.'
          : 'The last upgrade failed and was rolled back to the previous version.',
      detail: lifecycleMessage,
    };
  }

  const installed: Descriptor = {
    type: 'success',
    label: 'Installed',
    icon: <CheckCircle className="h-3 w-3 shrink-0" />,
  };

  // No upgrade issue and health not reported: show the lifecycle only.
  if (!healthStatus) {
    return installed;
  }

  switch (healthStatus) {
    case AddonHealthStatus.ADDON_HEALTH_HEALTHY:
      return {
        type: 'success',
        label: 'Running',
        icon: <CheckCircle className="h-3 w-3 shrink-0" />,
      };
    case AddonHealthStatus.ADDON_HEALTH_UNKNOWN:
      // Health could not be determined: muted rather than green, since green
      // would imply the addon is confirmed working when it is not.
      return {
        type: 'muted',
        label: 'Installed',
        icon: dot,
        summary: 'Installed. Health check status is currently unknown.',
      };
    default:
      return installed;
  }
}
